import { RepoFile } from "types/generated";

export type FileTreeNode = {
  name: string;
  path: string;
  type: "file" | "folder" | "parent";
  file?: RepoFile;
  children: FileTreeNode[];
  siblingCount: number;
};

const buildFileTree = (files: RepoFile[]): FileTreeNode[] => {
  const root: FileTreeNode[] = [];

  for (const file of files) {
    const segments = file.filePath.split("/");
    let currentLevel = root;

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const isFile = i === segments.length - 1;
      const path = segments.slice(0, i + 1).join("/");

      let existing = currentLevel.find((node) => node.name === segment);

      if (!existing) {
        existing = {
          name: segment,
          path,
          type: isFile ? "file" : "folder",
          children: [],
          siblingCount: 0,
          ...(isFile ? { file } : {}),
        };
        currentLevel.push(existing);
      }

      // Keep the most recent commit on folder nodes
      if (!isFile && (!existing.file || file.commit.createdAt > existing.file.commit.createdAt)) {
        existing.file = file;
      }

      currentLevel = existing.children;
    }
  }

  sortFileTree(root);
  assignSiblingCounts(root);

  return root;
};

const sortFileTree = (nodes: FileTreeNode[]): void => {
  nodes.sort((a, b) => {
    if (a.type !== b.type) {
      return a.type === "folder" ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });

  for (const node of nodes) {
    if (node.children.length > 0) {
      sortFileTree(node.children);
    }
  }
};

const assignSiblingCounts = (nodes: FileTreeNode[]): void => {
  for (const node of nodes) {
    node.siblingCount = nodes.length;
    if (node.children.length > 0) {
      assignSiblingCounts(node.children);
    }
  }
};

export const getFileTreeLevel = (tree: FileTreeNode[], path: string[]): FileTreeNode[] => {
  let level = tree;
  for (const segment of path) {
    const folder = level.find((n) => n.name === segment && n.type === "folder");
    if (!folder) return [];
    level = folder.children;
  }
  return level;
};

export default buildFileTree;
